import type { jsPDF } from 'jspdf';

import { drawTableCell } from '~/utils/pdf/pdfDrawHelpers';

import { generateQRCode } from './generateQRCode';
import { LABEL_LAYOUT } from './labelLayoutConfig';

/**
 * QR 코드 셀 그리기 (셀 + QR 이미지)
 *
 * @param pdf jsPDF 객체
 * @param x 셀 X 좌표
 * @param y 셀 Y 좌표
 * @param width 셀 너비
 * @param height 셀 높이
 * @param qrData QR 코드에 인코딩할 데이터
 * @param qrSize QR 코드 이미지 크기 (기본값: LABEL_LAYOUT.qr.size)
 */
export const drawQRCodeCell = async (
  pdf: jsPDF,
  x: number,
  y: number,
  width: number,
  height: number,
  qrData: string,
  qrSize: number = LABEL_LAYOUT.qr.size
): Promise<void> => {
  // QR 코드 셀 테두리 그리기
  drawTableCell(pdf, {
    x,
    y,
    width,
    height,
    borderColor: LABEL_LAYOUT.border.color,
    borderWidth: LABEL_LAYOUT.border.width,
    text: '',
  });

  // QR 코드 생성
  const qrCode = await generateQRCode(qrData);

  // QR 코드를 셀 중앙에 배치
  const qrX = x + (width - qrSize) / 2;
  const qrY = y + (height - qrSize) / 2;

  // QR 코드 이미지 삽입
  pdf.addImage(qrCode, 'PNG', qrX, qrY, qrSize, qrSize);
};

/**
 * 텍스트 길이에 따른 반응형 폰트 크기 및 오프셋 계산
 *
 * @param text 텍스트
 * @param config 반응형 폰트 설정 배열
 * @returns fontSize와 textOffsetY
 */
export const getResponsiveFontSize = (
  text: string,
  config: ReadonlyArray<{ readonly maxLength: number; readonly fontSize: number; readonly textOffsetY: number }>
): { fontSize: number; textOffsetY: number } => {
  const length = text.length;
  const setting = config.find((c) => length <= c.maxLength);

  return {
    fontSize: setting?.fontSize ?? 18,
    textOffsetY: setting?.textOffsetY ?? -9,
  };
};

/**
 * 텍스트 길이가 임계값을 초과하는지 확인하여 폰트 크기 반환
 *
 * @param text 텍스트
 * @param threshold 임계값
 * @param defaultSize 기본 폰트 크기
 * @param smallSize 작은 폰트 크기
 * @returns 폰트 크기
 */
export const getFontSizeByLength = (
  text: string,
  threshold: number,
  defaultSize: number,
  smallSize: number
): number => {
  return text.length > threshold ? smallSize : defaultSize;
};

/**
 * 라벨-값 행 설정
 */
export interface LabelValueRowConfig {
  label: string;
  value: string;
  labelWidth: number; // 그리드 단위
  valueWidth: number; // 그리드 단위
  labelOptions?: {
    fontSize?: number;
    textOffsetY?: number;
  };
  valueOptions?: {
    fontSize?: number;
    fontStyle?: 'normal' | 'bold';
    align?: 'left' | 'center';
  };
}

/**
 * 라벨 셀과 값 셀을 한 번에 그리는 헬퍼 함수
 * 라벨(헤더 배경)과 값을 가로로 배치
 *
 * @param pdf jsPDF 객체
 * @param x 시작 X 좌표
 * @param y Y 좌표
 * @param gridWidth 1그리드 너비
 * @param rowHeight 행 높이
 * @param config 라벨-값 설정
 */
export const drawLabelValueRow = (
  pdf: jsPDF,
  x: number,
  y: number,
  gridWidth: number,
  rowHeight: number,
  config: LabelValueRowConfig
): void => {
  // 라벨 셀 그리기
  drawTableCell(pdf, {
    x,
    y,
    width: gridWidth * config.labelWidth,
    height: rowHeight,
    fillColor: LABEL_LAYOUT.colors.headerBg,
    borderColor: LABEL_LAYOUT.border.color,
    borderWidth: LABEL_LAYOUT.border.width,
    text: config.label,
    fontSize: config.labelOptions?.fontSize ?? LABEL_LAYOUT.fonts.medium,
    fontStyle: 'normal',
    align: 'center',
    textOffsetX: 0,
    textOffsetY: config.labelOptions?.textOffsetY,
  });

  // 값 셀 그리기
  drawTableCell(pdf, {
    x: x + gridWidth * config.labelWidth,
    y,
    width: gridWidth * config.valueWidth,
    height: rowHeight,
    borderColor: LABEL_LAYOUT.border.color,
    borderWidth: LABEL_LAYOUT.border.width,
    text: config.value,
    fontSize: config.valueOptions?.fontSize ?? LABEL_LAYOUT.fonts.medium,
    fontStyle: config.valueOptions?.fontStyle,
    align: config.valueOptions?.align,
  });
};
